/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  type GetServerSidePropsContext,
  type GetStaticPropsContext,
  type NextApiRequest,
  type NextPageContext,
} from 'next';
import { getDecodedCookies, parseVIdFromUtagMain, getCookies } from './cookie';
import { COOKIES } from '@/constants/cookies';
import { defaultState } from '@/stores/AppState';
import type { AppState } from '@/stores/interface';
import { countryCodes } from '@/constants/locale';
import type { Locale } from '@/types/i18n';
import { handleEpsilonCookie } from './handleEpsilonCookie.server';

/**
 * This function is placed in utils instead of @/stores/AppState as this is server only,
 * but AppState is universal
 *
 * NOTE: Never return undefined for any state value. Instead return null as undefined is not serializable
 */
export async function getInitialAppState(
  ctx: GetServerSidePropsContext | GetStaticPropsContext,
): Promise<AppState | null> {
  // Skip loading initial App State on client side. This is a hack until server components are stable
  if ('req' in ctx && ctx.req.url?.startsWith('/_next/data')) {
    return null;
  } else if ('req' in ctx) {
    return await getInitialAppStateFromRequest(ctx);
  }

  // There is NO 'req' in context. Called from getStaticProps or getStaticPaths
  return getInitialAppStateWithoutRequest();
}

async function getInitialAppStateFromRequest({
  req,
  res,
}: GetServerSidePropsContext): Promise<AppState> {
  const cookies = await getDecodedCookies([COOKIES.userId, COOKIES.rewardsId], { req, res });
  const rewardsId = cookies.get(COOKIES.rewardsId) as string | null;
  const proxiedHostname = getProxiedHostname(req);
  const locale = getLocaleForProxiedHostname(proxiedHostname);
  const userAgent = req.headers['user-agent'];

  /**
   *  Replaced | (OR) conditions with a non-capturing group (?:\.\d+){0,2} to match additional .x parts in the version number. This allows for matching 2 to 4 segments in the version number.
      Added escapes \ before the dots (.) to ensure they are treated as literal dots instead of the regex wildcard character. This is important for matching version numbers correctly.
      This pattern is more concise and avoids unnecessary alternatives, which should improve its efficiency.
   */
  const isMobileApp =
    /AutoZone\/(\d+\.\d+(?:\.\d+){0,2}) \((Android|iOS) (\d+\.\d+(?:\.\d+){0,2})\)/i.test(
      req.headers['user-agent'] ?? '',
    );
  const eCookieId = await handleEpsilonCookie({ req, res });

  // Extract cookies for elasticsearch
  const cookieStore = await getCookies({ req, res });
  const utagMain = cookieStore['utag_main'] || '';
  const utagVisitorIdCookie = cookieStore['utag_main_v_id'];
  const visitorId = parseVIdFromUtagMain(utagMain) || utagVisitorIdCookie || '';

  // example: "24.4.0" from "Mozilla/5.0 (iPhone; CPU iPhone OS 17_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148 AutoZone/24.4.0 (iOS 17.4)"
  const mobileAppVersion = userAgent?.split(
    /AutoZone\/(\d+\.\d+\.\d+)\s\((Android|iOS)\s\d+(\.\d+)*\)/,
  )[1];

  return {
    ...defaultState,
    eCookieId,
    locale,
    visitorId,
    rewardsId,
    proxiedHostname,
    isMobileApp,
    mobileAppVersion,
  };
}

function getInitialAppStateWithoutRequest(): AppState {
  return {
    ...defaultState,
  };
}

export function getProxiedHostname(req: NextPageContext['req'] | NextApiRequest): string | null {
  if (!req) {
    return null;
  }

  const forwardedHost = req.headers['x-forwarded-host'] as string | undefined;
  const host = req.headers['host'];
  const refererProtocol = req.headers.referer?.split('://')[0];
  const protocol =
    'protocol' in req && typeof req.protocol === 'string'
      ? req.protocol
      : (refererProtocol ?? 'https');

  if (forwardedHost) {
    return new URL(`${protocol}://${forwardedHost}`).hostname;
  }

  if (host) {
    return new URL(`${protocol}://${host}`).hostname;
  }

  if (!req.url) {
    return null;
  }

  let url;

  try {
    url = new URL(req.url);
  } catch {
    return null;
  }

  return url.hostname;
}

export function getLocaleForProxiedHostname(proxiedHostname: string | null): Locale {
  if (proxiedHostname?.endsWith('mx')) {
    return countryCodes.mx;
  }

  if (proxiedHostname?.endsWith('br')) {
    return countryCodes.ptBr;
  }

  return countryCodes.enUS;
}
